import { lazy } from 'react';

import type { GetStaticPaths, GetStaticProps } from 'next';

import Button from 'epn-ui/umd/dist/components/Button';
import Loader from 'epn-ui/umd/dist/components/Loader';

import { toast } from 'react-toastify';

import api from '@api';
import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import routes from '@config/routes.json';
import useI18n, { getLocalePaths } from '@i18n';

import BaseToast from '@base/BaseToast';

import { IGetFeedsDataFormatted, IGetOffersDataFormatted } from '@api/types';

// HOCs
import WithRole, { rolesEnum } from '@HOCs/WithRole';

const { useFeeds, useOffers } = apiHooks;

const FeedsTable = lazy(() => import('@pages/tools/feeds/Table/insex'));
const Feeds: React.FC = () => {
    const { t, locale } = useI18n('Feeds');
    const { t: tErrors } = useI18n('Errors');
    const {
        data: feeds,
        mutate: mutateFeeds,
        isLoading: isFeedsLoading,
        isReachingEnd,
        nextPage,
    } = useFeeds({
        params: {
            limit: 10,
            sort: '-created_at',
        },
    }) as ICreateUseRequestInfiniteResponse<IGetFeedsDataFormatted[]>;

    const { data: offers, isLoading: isOffersLoading } = useOffers({
        condition: feeds.length > 0,
        params: {
            limit: feeds.length,
            lang: locale,
            viewRules: 'role_user',
            ids: feeds.map(feed => feed.offerId),
        },
    }) as ICreateUseRequestInfiniteResponse<IGetOffersDataFormatted[]>;

    const onDeleteOfferById = async (id: number) => {
        try {
            await api.feeds.deleteFeed({
                urlParams: {
                    id,
                },
            });

            await mutateFeeds();

            toast.success(<BaseToast text={t('Feed was deleted')} type="success" />, {
                toastId: id,
            });
        } catch (e) {
            toast.success(<BaseToast text={tErrors('Something went wrong')} type="error" />, {
                toastId: id,
            });
        }
    };

    return (
        <div className="min-h-[60vh] mb-[100px]">
            <div className="flex flex-col items-start justify-between xs:items-center xs:flex-row">
                <h4 className="font-medium text-blueGray-600 text-h2">{t('Feed list')}</h4>
                <Button
                    className="w-full mt-4 xs:mt-0 xs:w-auto"
                    type="primary"
                    size="middle"
                    href={`/${locale}${routes.tools.feed.create}`}
                >
                    {t('Create uploading')}
                </Button>
            </div>
            <p className="mt-6 text-blueGray-600 text-p1">
                {t(
                    'Download with feed of products designed to work with a lot of products. You can create affiliate stores based on these downloads, or you can use them in contextual advertising systems, automatically forming ads.',
                )}
            </p>
            <div className="mt-[24px]">
                <Loader loading={isFeedsLoading} className="mt-[100px]">
                    {!feeds.length ? (
                        <div className="text-center text-blueGray-800 py-[3rem] bg-blueGray-100 rounded-[6px]">
                            <p className="mb-[1.5rem]">
                                {t('Create your first uploading. To create it, click a button')}
                            </p>
                            <Button
                                className="xs:mt-0"
                                type="primary"
                                size="middle"
                                href={`/${locale}${routes.tools.feed.create}`}
                            >
                                {t('Create uploading')}
                            </Button>
                        </div>
                    ) : (
                        <FeedsTable
                            offers={offers}
                            feeds={feeds}
                            nextPage={nextPage}
                            onDeleteOfferById={onDeleteOfferById}
                            isReachingEnd={isReachingEnd}
                            isTableLoading={isFeedsLoading || isOffersLoading}
                        />
                    )}
                </Loader>
            </div>
        </div>
    );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async () => {
    return { paths: getLocalePaths(), fallback: false };
};

export default WithRole(Feeds, [rolesEnum.user]);
